import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Package, Globe, Target, MessageCircle, CheckCircle, Clock, ChevronDown, ChevronRight,
  Search, RefreshCw, ExternalLink, ShoppingBag, CreditCard,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColor = (status) => {
  switch (status) {
    case 'Completed':
    case 'Live':
    case 'active':
    case 'Approved':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'Pending':
    case 'In Progress':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'Over':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-600 border-slate-200';
  }
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch (e) { return String(d); }
};

const ClientOnboardingManagement = () => {
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/client-setups`);
      setSetups(res.data || []);
    } catch (err) {
      console.error('Fetch client setups error:', err);
      setSetups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = setups.filter((s) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      s.user?.name?.toLowerCase().includes(q) ||
      s.user?.email?.toLowerCase().includes(q) ||
      s.user?.phone?.includes(q) ||
      s.user?.clientId?.toLowerCase().includes(q) ||
      s.plan?.toLowerCase().includes(q)
    );
  });

  const toggle = (uid) => setExpanded((e) => ({ ...e, [uid]: !e[uid] }));

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-blue-600" />
              Client Onboarding Setups
            </CardTitle>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Every client&apos;s submitted onboarding data — products, Shopify, ads budget, WhatsApp.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="onboarding-search-input"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchData}
              data-testid="onboarding-refresh-btn"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && (
            <div className="text-center py-12 text-slate-500 text-sm">Loading client setups…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500 text-sm">
              No client onboarding records yet. They&apos;ll appear here once users complete the wizard after payment.
            </div>
          )}

          {!loading && filtered.map((s) => {
            const isOpen = !!expanded[s.userId];
            const prog = s.progress || {};
            const wizardComplete = prog.whatsappStatus === 'Completed';
            return (
              <div
                key={s.userId}
                className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
                data-testid={`onboarding-row-${s.userId}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(s.userId)}
                  className="w-full flex items-center justify-between gap-3 p-3 sm:p-4 hover:bg-slate-50 text-left transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0">
                      {(s.user?.name || s.userId || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 text-sm sm:text-base truncate">
                          {s.user?.name || (s.userId ? `User ${String(s.userId).slice(-6)}` : '—')}
                        </span>
                        {s.user?.clientId && (
                          <span className="text-[10px] sm:text-xs text-slate-500 font-mono">#{s.user.clientId}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {s.user?.email || s.userId || '—'} · {s.user?.phone || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className={statusColor(s.plan)}>
                      <Package className="w-3 h-3 mr-1" /> {s.plan || '—'}
                    </Badge>
                    <Badge variant="outline" className={statusColor(wizardComplete ? 'Completed' : 'Pending')}>
                      {wizardComplete ? <CheckCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                      {wizardComplete ? 'Wizard done' : 'In progress'}
                    </Badge>
                  </div>
                  {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          : <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-200 bg-slate-50/50 p-3 sm:p-4 space-y-4">
                    {/* Mobile-only summary badges */}
                    <div className="flex sm:hidden items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={statusColor(s.plan)}>
                        <Package className="w-3 h-3 mr-1" /> {s.plan || '—'}
                      </Badge>
                      <Badge variant="outline" className={statusColor(wizardComplete ? 'Completed' : 'Pending')}>
                        {wizardComplete ? 'Wizard done' : 'In progress'}
                      </Badge>
                    </div>

                    {/* Selected products */}
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <ShoppingBag className="w-3.5 h-3.5" /> Selected Products ({(s.products || []).length})
                      </div>
                      {(s.products || []).length === 0 ? (
                        <div className="text-xs text-slate-400">No products selected yet</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                          {(s.products || []).map((p, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2 text-xs"
                            >
                              {p?.image && (
                                <img src={p.image} alt={p?.name || ''} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                              )}
                              <span className="truncate text-slate-700">{typeof p === 'string' ? p : (p?.name || p?.id || '—')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Shopify */}
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <Globe className="w-3.5 h-3.5" /> Shopify Details
                      </div>
                      {!s.shopifyCredentials ? (
                        <div className="text-xs text-slate-400">Skipped or not provided</div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-white border border-slate-200 rounded-lg p-3">
                          {Object.entries(s.shopifyCredentials).filter(([k]) => k !== 'skipped').map(([k, v]) => (
                            <div key={k} className="flex flex-col">
                              <span className="text-slate-500 text-[10px] uppercase tracking-wide">{k}</span>
                              <span className="text-slate-800 font-medium break-all">{String(v || '—')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Ads budget */}
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <Target className="w-3.5 h-3.5" /> Ads Budget
                      </div>
                      {!s.adsBudget ? (
                        <div className="text-xs text-slate-400">Not set yet</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-white border border-slate-200 rounded-lg p-3">
                          <div>
                            <div className="text-slate-500 text-[10px] uppercase">Daily Total</div>
                            <div className="font-bold text-slate-800">₹{(s.adsBudget.total || 0).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-slate-500 text-[10px] uppercase">Per Product</div>
                            <div className="font-bold text-slate-800">₹{(s.adsBudget.perProduct || 0).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-slate-500 text-[10px] uppercase">Days</div>
                            <div className="font-bold text-slate-800">{s.adsBudget.days || '—'}</div>
                          </div>
                          <div>
                            <div className="text-slate-500 text-[10px] uppercase">Campaign Total</div>
                            <div className="font-bold text-slate-800">₹{(s.adsBudget.totalCampaign || 0).toLocaleString()}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Progress */}
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Progress</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        <div className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                          <CreditCard className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-slate-500 text-[10px]">Purchase</div>
                            <Badge variant="outline" className={`${statusColor(prog.purchaseStatus)} text-[10px] py-0`}>{prog.purchaseStatus}</Badge>
                          </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                          <MessageCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-slate-500 text-[10px]">WhatsApp</div>
                            <Badge variant="outline" className={`${statusColor(prog.whatsappStatus)} text-[10px] py-0`}>{prog.whatsappStatus}</Badge>
                          </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-slate-500 text-[10px]">Website</div>
                            <Badge variant="outline" className={`${statusColor(prog.websiteStatus)} text-[10px] py-0`}>{prog.websiteStatus}</Badge>
                          </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                          <Target className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-slate-500 text-[10px]">Campaign</div>
                            <Badge variant="outline" className={`${statusColor(prog.campaignStatus)} text-[10px] py-0`}>{prog.campaignStatus}</Badge>
                          </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                          <Package className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-slate-500 text-[10px]">Ads</div>
                            <Badge variant="outline" className={`${statusColor(prog.adsStatus)} text-[10px] py-0`}>{prog.adsStatus}</Badge>
                          </div>
                        </div>
                        {prog.websiteLink && (
                          <a
                            href={prog.websiteLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center gap-1 text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate font-medium">{prog.websiteLink}</span>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1 pt-2 border-t border-slate-200">
                      <span>Created: <span className="text-slate-700">{fmtDate(s.createdAt)}</span></span>
                      <span>Updated: <span className="text-slate-700">{fmtDate(s.updatedAt)}</span></span>
                      {prog.whatsappConnectedAt && (
                        <span>WhatsApp at: <span className="text-green-600">{fmtDate(prog.whatsappConnectedAt)}</span></span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientOnboardingManagement;
